import { useEffect, useRef } from 'react';

import { useApi } from './useApi';
import useAuthStore from '../stores/authStore';
import useJobProgressStore from '../stores/jobProgressStore';
import { getAdapter } from '../lib/jobAdapters';
import { isTerminal, mapStatus } from '../lib/jobStatus';

const POLL_MS = 2500;
// Drop a job after this many consecutive failed polls (~2 min at 2.5s). A job
// whose status endpoint keeps erroring (auth revoked, server purged the row
// without a 404, …) must never poll forever.
const MAX_CONSECUTIVE_FAILURES = 48;

const is404 = err => err?.status === 404 || err?.response?.status === 404 || /\b404\b/.test(err?.message || '');
const is401 = err => err?.status === 401 || err?.response?.status === 401 || /\b401\b/.test(err?.message || '');

/**
 * The single global poller for the job-progress UX. Mounted once (in
 * GlobalJobProgress). Every 2.5s it polls each ACTIVE, pollable job (admin-tool
 * jobs — `upload` is `externallyFed` by useUploadJobsWatcher) via its adapter's
 * status endpoint and merges the fresh payload into the store. A 404 means the
 * job is gone/forbidden (require_admin returns 404, not 403) → drop it. Terminal
 * display + auto-dismiss are handled by the toast/modal, not here.
 *
 * Guardrails (2026-07-04 prod incident — a stale persisted job polled the API
 * with 401s several times a second from the LOGIN page for hours):
 * - No auth → no polling. GlobalJobProgress mounts outside the auth gate, and
 *   each unauthenticated poll also fired a token-refresh attempt, multiplying
 *   the request flood.
 * - A 401 mid-session pauses that tick (useApi's refresh already retried once);
 *   consecutive failures beyond MAX_CONSECUTIVE_FAILURES drop the job.
 */
export default function useJobProgressPoller() {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const failuresRef = useRef({}); // jobId -> consecutive failed polls

  useEffect(() => {
    let cancelled = false;
    const failures = failuresRef.current;

    const pollable = () =>
      Object.values(useJobProgressStore.getState().jobs).filter(job => {
        const adapter = getAdapter(job.kind);
        return !adapter.externallyFed && adapter.statusPath && mapStatus(job.raw?.status) === 'active';
      });

    const tick = async () => {
      // Hard gate: never poll while logged out — the login page must not
      // generate a 401-refresh-401 request storm for stale persisted jobs.
      if (!useAuthStore.getState().isUserAuthenticated) return;
      const jobs = pollable();
      if (!jobs.length) return;
      const { updateJob, markNotified, removeJob } = useJobProgressStore.getState();
      await Promise.all(
        jobs.map(async job => {
          const adapter = getAdapter(job.kind);
          try {
            const raw = await apiRef.current.get(adapter.statusPath(job.id));
            if (cancelled || !raw) return;
            delete failures[job.id];
            updateJob(job.id, raw);
            if (isTerminal(raw.status) && !job._notified) markNotified(job.id);
          } catch (err) {
            if (is404(err)) {
              delete failures[job.id];
              removeJob(job.id);
              return;
            }
            // 401 here means useApi's refresh+retry already failed — the
            // session is gone. Don't drop the job (it may still be running
            // server-side); the auth gate stops further polling until login.
            if (is401(err)) return;
            failures[job.id] = (failures[job.id] || 0) + 1;
            if (failures[job.id] >= MAX_CONSECUTIVE_FAILURES) {
              delete failures[job.id];
              removeJob(job.id);
            }
          }
        })
      );
    };

    const interval = setInterval(tick, POLL_MS);
    tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);
}
